import { isEmpty, isNil } from 'lodash-es';
import { DatexFormControl } from './datex-form-control';
import { Styles } from './style';
import { ValidationErrors, Validators } from '@angular/forms';
import vkbeautify from 'vkbeautify';
import { EventEmitter } from '@angular/core';

export class ControlModel {
  styles: Styles;

  constructor(styles?: any) {
    this.styles = isNil(styles) ? new Styles() : styles;
  }
}

export class ValueControlModel<TValue = any> extends ControlModel {
  protected formControl: DatexFormControl<TValue>;
  protected tooltipGetter?: () => string;
  public validationTriggered: EventEmitter<void> = new EventEmitter<void>;

  constructor(formControl: DatexFormControl<TValue>, styles?: Styles, readOnly?: boolean, disabled?: boolean, tooltipGetter?: () => string) {
    super(styles);
    this.formControl = formControl;
    this.readOnly = readOnly
    this.disabled = disabled;
    this.tooltipGetter = tooltipGetter;
  }

  get defaultValue(): TValue {
    return this.formControl.defaultValue;
  }

  get value(): TValue {
    return this.formControl.value;
  }

  get errors() {
    return this.formControl.errors;
  }

  get touched() {
    return this.formControl.touched;
  }

  get errorMessages() {
    const builtInValidators = Object.getOwnPropertyNames(Validators);
    const errors = Object.entries(this.errors ?? {});
    const customErrors = errors?.filter(i => !builtInValidators.includes(i[0])).map(i => i[1]);
    const angularErrors = errors?.filter(i => builtInValidators.includes(i[0])).map(i => {
      switch (i[0]) {
        case 'required':
          return 'This field is required.';
        default:
          return i[1];
      }
    });

    return (customErrors ?? []).concat(angularErrors ?? []);
  }

  set value(v: TValue) {
    this.formControl.setValue(v, { emitEvent: false });
    this.formControl.markAsDirty();
  }

  get valid() {
    return this.formControl.valid;
  }

  get valueChanges() {
    return this.formControl.valueChanges
  }

  setErrors(errors: ValidationErrors) {
    this.formControl.setErrors(errors);
  }

  validate() {
    this.validationTriggered.emit();
  }

  // TODO: To discuss if we want to use reset as the initial value
  reset(v: TValue) {
    this.formControl.reset(v, { emitEvent: false });
  }

  private _readonly: boolean = false;
  private _disabled: boolean = false;

  get readOnly() {
    return this._readonly;
  }

  set readOnly(val: boolean) {
    this._readonly = val;
    this._updateControlState();
  }

  get disabled() {
    return this._disabled;
  }

  set disabled(val: boolean) {
    this._disabled = val;
    this._updateControlState();
  }

  private _updateControlState() {
    if (this._readonly || this._disabled) {
      this.formControl.disable({ emitEvent: false });
    } else {
      this.formControl.enable({ emitEvent: false });
    }
  }

  /**
   * @description
   * A flag indicated whether the control value has changed. 
   * IMPORTANT to note that it will be marked as changed only on UI changes.
   * It relies on the FormControl dirty flag
   *
   */
  get isChanged(): boolean {
    return this.formControl.dirty;
  }

  focus() {
    this.formControl.focus();
  }

  blur() {
    this.formControl.blur();
  }

  get tooltip() {
    if (this.tooltipGetter) {
      return this.tooltipGetter();
    }

    return this.value?.toString();
  }
}

export class TextBoxModel extends ValueControlModel<string> {
  placeholder = '';

  constructor(formControl: DatexFormControl<string>, styles?: Styles, readOnly?: boolean, disabled?: boolean, placeholder?: string, tooltipGetter?: () => string) {
    super(formControl, styles, readOnly, disabled, tooltipGetter);
    this.placeholder = placeholder;
  }
}

export class NumberBoxModel extends ValueControlModel<number> {
  formatGetter?: () => string;
  placeholder = '';

  constructor(formControl: DatexFormControl<number>, styles?: Styles, readOnly?: boolean, disabled?: boolean, formatGetter?: () => string, placeholder?: string, tooltipGetter?: () => string) {
    super(formControl, styles, readOnly, disabled, tooltipGetter);
    this.formatGetter = formatGetter;
    this.placeholder = placeholder;
  }

  get format() {
    if (this.formatGetter) {
      return this.formatGetter();
    }
    return null;
  }
}

export enum ESelectBoxType {
  dropdown = 'dropdown',
  chips = 'chips',
  radioButtonsCheckboxes = 'radioButtonsCheckboxes'
}

export class SelectBoxModel extends ValueControlModel<any> {
  type?: ESelectBoxType;
  placeholder = '';
  displayText = '';

  constructor(formControl: DatexFormControl<any>, type?: ESelectBoxType, styles?: Styles, readOnly?: boolean, disabled?: boolean, placeholder?: string, tooltipGetter?: () => string) {
    super(formControl, styles, readOnly, disabled, tooltipGetter);
    this.type = type;
    this.placeholder = placeholder;
  }
}

export class DateBoxModel extends ValueControlModel<string> {
  formatGetter: () => string;
  mode: string;

  constructor(formControl: DatexFormControl<string>, styles?: Styles, readOnly?: boolean, disabled?: boolean, formatGetter?: () => string, mode?: string, tooltipGetter?: () => string) {
    super(formControl, styles, readOnly, disabled, tooltipGetter);
    this.formatGetter = formatGetter;
    this.mode = mode;
  }

  get format() {
    if (this.formatGetter) {
      return this.formatGetter();
    }
    return null;
  }
}

export class CheckBoxModel extends ValueControlModel<boolean> {
  label: string;
  constructor(formControl: DatexFormControl<boolean>, styles?: Styles, readOnly?: boolean, disabled?: boolean, label?: string, tooltipGetter?: () => string) {
    super(formControl, styles, readOnly, disabled, tooltipGetter);
    this.label = label;
  }
}

export class TextModel extends ControlModel {
  text: string = null;
  formatType: string = null;
  formatGetter: () => string = null;
  disabled: boolean = false;
  tooltipGetter?: () => string;

  constructor(styles?: Styles, text?: string, formatType?: string, formatGetter?: () => string, tooltipGetter?: () => string) {
    super(styles);
    this.text = text;
    this.formatType = formatType;
    this.formatGetter = formatGetter;
    this.tooltipGetter = tooltipGetter;
  }

  get tooltip() {
    if (this.tooltipGetter) {
      return this.tooltipGetter();
    }

    return this.text;
  }

  get format() {
    if (this.formatGetter) {
      return this.formatGetter();
    }
    return null;
  }
}

export class LabelModel extends ControlModel {
  label: string = null;
  disabled: boolean = false;
  tooltipGetter?: () => string;

  constructor(styles?: Styles, label?: string, tooltipGetter?: () => string) {
    super(styles);
    this.label = label;
    this.tooltipGetter = tooltipGetter;
  }

  get tooltip() {
    if (this.tooltipGetter) {
      return this.tooltipGetter();
    }

    return this.label;
  }
}

export class ButtonStyles extends Styles {
  private setStateClass(klass: string) {
    this.removeClass([
      'primary',
      'secondary',
      'tertiary',
      'destructive',
      'creation',
      'link']);
    this.addClass(klass);
  }

  setPrimaryClass() { this.setStateClass('primary'); }
  setSecondaryClass() { this.setStateClass('secondary'); }
  setTertiaryClass() { this.setStateClass('tertiary'); }
  setDestructiveClass() { this.setStateClass('destructive'); }
  setCreationClass() { this.setStateClass('creation'); }
  setLinkClass() { this.setStateClass('link'); }
}

export class ButtonModel extends ControlModel {
  declare styles: ButtonStyles;
  private _readOnly = false;
  private _disabled = false;
  removed: boolean = false;
  id?: string;
  label?: string;
  icon?: string;
  tooltipGetter?: () => string;

  constructor(id?: string, styles?: ButtonStyles, readOnly?: boolean, disabled?: boolean, removed?: boolean, label?: string, icon?: string, tooltipGetter?: () => string) {
    super(null);
    this.styles = isNil(styles) ? new ButtonStyles() : styles;
    this.readOnly = readOnly;
    this.disabled = disabled;;
    this.removed = removed;
    this.id = id;
    this.label = label;
    this.icon = icon;
    this.tooltipGetter = tooltipGetter;
  }

  get readOnly() {
    return this._readOnly;
  }

  set readOnly(val: any) {
    this._readOnly = val;
  }

  get disabled() {
    return this._disabled;
  }

  set disabled(val: any) {
    this._disabled = val;
  }

  get tooltip() {
    if (this.tooltipGetter) {
      return this.tooltipGetter();
    }

    return this.label;
  }
}

export class SplitButtonModel<T extends { [K in keyof T]: ButtonModel }> extends ButtonModel {
  buttons: T;

  constructor(
    id?: string,
    styles?: ButtonStyles,
    readOnly?: boolean,
    disabled?: boolean,
    label?: string,
    icon?: string,
    tooltipGetter?: () => string,
    buttons?: ButtonModel[]) {
    super(id, styles, readOnly, disabled, false, label, icon, tooltipGetter);
    this.buttons = Object.fromEntries(buttons?.map(i => [i.id, i]) ?? []) as T;
  }
}

export class ImageModel extends ControlModel {
  src: string = null;
  disabled: boolean = false;
  tooltipGetter?: () => string;

  constructor(styles?: Styles, src?: string, tooltipGetter?: () => string) {
    super(styles);
    this.src = src;
    this.tooltipGetter = tooltipGetter;
  }

  get tooltip() {
    if (this.tooltipGetter) {
      return this.tooltipGetter();
    }

    return this.src;
  }
}

export class SeparatorModel extends ControlModel {
  constructor(styles?: Styles) {
    super(styles);
  }
}
export class DrawModel extends ValueControlModel<string> {
  options: {};

  constructor(formControl: DatexFormControl<string>, styles?: Styles, readOnly?: boolean, disabled?: boolean, options?: {}, tooltipGetter?: () => string) {
    super(formControl, styles, readOnly, disabled, tooltipGetter);
    this.options = options;
  }
}

export class CodeBoxModel extends ValueControlModel<string> {
  codeMirrorMode?: string;
  mode?: string;

  constructor(formControl: DatexFormControl<string>, styles?: Styles, readOnly?: boolean, disabled?: boolean, mode?: string, tooltipGetter?: () => string) {
    super(formControl, styles, readOnly, disabled, tooltipGetter);
    this.mode = mode;
    if (mode === 'xml') {
      this.codeMirrorMode = 'application/xml';
    } else if (mode === 'json') {
      this.codeMirrorMode = 'application/json';
    }
  }

  beautify(): void {
    try {
      if (this.mode === 'xml') {
        this.value = vkbeautify.xml(this.value);
      } else {
        this.value = vkbeautify.json(this.value);
      }
    } catch (e) {
      console.error(e);
    }
  }

  minify(): void {
    try {
      if (this.mode === 'xml') {
        this.value = vkbeautify.xmlmin(this.value, [, true]);
      } else {
        this.value = vkbeautify.jsonmin(this.value);
      }
    } catch (e) {
      console.error(e);
    }
  }

  // NOTE: the following is an implementation of automatic beautify/minify when getting/setting the value
  // it's commented out since getting the value is happening a lot, mainly due to change detection
  // 
  // beautify() {
  //   this.value = super.value;
  // }

  // minify() {
  //   super.value = this.value;
  // }

  // override get value() {
  //   if (isEmpty(super.value)) {
  //     return super.value;
  //   }

  //   try {
  //     return this.mode === 'xml' ? vkbeautify.xmlmin(super.value, [, true]) : vkbeautify.jsonmin(super.value);
  //   } catch (e) {
  //     console.error(e);
  //     return super.value;
  //   }
  // }

  // override set value(v) {
  //   let val;
  //   if (!isEmpty(v)) {
  //     try {
  //       val = this.mode === 'xml' ? vkbeautify.xml(v) : vkbeautify.json(v);
  //     } catch (e) {
  //       console.error(e);
  //       val = v;
  //     }
  //   }

  //   super.value = val;
  // }
}